import {MessageDescriptor} from '@lingui/core';
import {t} from '@lingui/macro';
import {first} from 'lodash';
import {createSelector} from 'reselect';

import {buildContactDisplayName} from '../../../helpers/models/contactHelpers';
import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {MessageTypesEnum} from '../../../models/api/messageModel';
import {getLocalize} from '../../stateless/internationalizationSelectors';
import {makeGetVisualRecipient} from '../contacts/recipientsSelectors';
import {makeGetExternalChannelTypeFromConversation} from '../externalChannelTypes/externalChannelTypesSelectors';
import {getInboxes} from '../inboxes/inboxesSelectors';
import {
  ConversationBaseSelector,
  ConversationSelector,
  makeGetLastDraftMessageSummary,
} from './conversationsSelectors';

/*
 * Types.
 */

export interface ConversationSubjectModel {
  text: string;
  isPlaceholder: boolean;
}

export type ConversationSubjectSelector<P> = AppParametricSelector<P, ConversationSubjectModel>;

/*
 * Selectors.
 */

function makeGetInbox<P>(getConversation: ConversationBaseSelector<P>) {
  return createSelector([getConversation, getInboxes], (conversation, inboxes) => {
    const inboxId = first(conversation.inboxIds);
    return (inboxId && inboxes[inboxId]) || undefined;
  });
}

export function makeGetConversationSubject<P>(
  getConversation: ConversationSelector<P>,
  getTeammateId: AppParametricSelector<P, number>,
): ConversationSubjectSelector<P> {
  const getRecipient = makeGetVisualRecipient(getConversation, getTeammateId);
  const getInbox = makeGetInbox(getConversation);
  const getDraft = makeGetLastDraftMessageSummary(getConversation);

  return createSelector(
    [
      getLocalize,
      getConversation,
      getRecipient,
      getInbox,
      getDraft,
      makeGetExternalChannelTypeFromConversation(getConversation),
    ],
    (localize, conversation, recipient, inbox, draft, externalChannelType): ConversationSubjectModel => {
      const {numMessages, messageType} = conversation;

      const conversationMessageType = (numMessages === 0 && draft && draft.type) || messageType;
      const subject = (conversation.subject || (draft && draft.subject) || '').trim();

      const isSubjectSupportedByMessageType =
        conversationMessageType === MessageTypesEnum.EMAIL ||
        conversationMessageType === MessageTypesEnum.INTERNAL ||
        conversationMessageType === MessageTypesEnum.CUSTOM;
      const shouldUseSubject =
        (isSubjectSupportedByMessageType || conversation.hasOverriddenSubject) && subject;

      // If we have a valid subject to render.
      if (shouldUseSubject) {
        return {
          text: subject,
          isPlaceholder: false,
        };
      }

      // If we have a custom channel and found a channel with a external type and a recipient.
      if (conversationMessageType === MessageTypesEnum.CUSTOM && externalChannelType && recipient) {
        return {
          text: localize(
            t('conversation-prefix-partner-channel')`${
              externalChannelType.name
            } conversation with ${buildContactDisplayName(recipient)}`,
          ),
          isPlaceholder: false,
        };
      }

      // Otherwise, render the recipient.
      // If we have a recipient, use it.
      if (recipient) {
        return {
          text: localize(subjectPrefixes[messageType](buildContactDisplayName(recipient))),
          isPlaceholder: false,
        };
      }

      // If we have an inbox, use that instead.
      if (inbox) {
        return {
          text: inbox.name,
          isPlaceholder: false,
        };
      }

      // Otherwise, render a placeholder.
      return {
        text: localize(subjectPrefixes[MessageTypesEnum.EMAIL]('')),
        isPlaceholder: true,
      };
    },
  );
}

/*
 * Localization.
 */

type SubjectPrefixes = {[T in MessageTypesEnum]: (recipientName: string) => MessageDescriptor};
// prettier-ignore
const subjectPrefixes: SubjectPrefixes = {
  [MessageTypesEnum.INTERNAL]: () => t('conversation-placeholder-internal')`No topic`,
  [MessageTypesEnum.EMAIL]: () => t('conversation-placeholder-email')`No subject`,
  [MessageTypesEnum.TWEET]: rn => t('conversation-prefix-twitter')`Twitter conversation with ${rn}`,
  [MessageTypesEnum.TWEET_DM]: rn => t('conversation-prefix-twitterdm')`Twitter DM with ${rn}`,
  [MessageTypesEnum.CALL]: rn => t('conversation-prefix-call')`Phone call from ${rn}`,
  [MessageTypesEnum.SMS]: rn => t('conversation-prefix-sms')`SMS thread with ${rn}`,
  [MessageTypesEnum.GOOGLE_PLAY]: rn => t('conversation-prefix-googleplay')`Google Play review from ${rn}`,
  [MessageTypesEnum.FACEBOOK]: rn => t('conversation-prefix-facebook')`Facebook conversation with ${rn}`,
  [MessageTypesEnum.INTERCOM]: rn => t('conversation-prefix-intercom')`Intercom conversation with ${rn}`,
  [MessageTypesEnum.SMOOCH]: rn => t('conversation-prefix-smooch')`Smooch conversation with ${rn}`,
  [MessageTypesEnum.LAYER]: rn => t('conversation-prefix-chat')`Chat conversation with ${rn}`,
  [MessageTypesEnum.YALO_WHATSAPP]: rn => t('conversation-prefix-whatsapp')`WhatsApp thread with ${rn}`,
  [MessageTypesEnum.WHATSAPP]: rn => t('conversation-prefix-whatsapp')`WhatsApp thread with ${rn}`,
  [MessageTypesEnum.CUSTOM]: rn => t('conversation-prefix-custom')`Custom conversation with ${rn}`,
  [MessageTypesEnum.FRONT_CHAT]: rn => t('conversation-prefix-frontchat')`Chat conversation with ${rn}`
};
